import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { productsAPI, categoriesAPI } from '../../utils/api';
import { ProductCard, LoadingSpinner, EmptyState } from '../../components/common';

const ProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [expandedCats, setExpandedCats] = useState({});

  const category = searchParams.get('category') || '';
  const subcategory = searchParams.get('subcategory') || '';
  const search = searchParams.get('search') || '';

  useEffect(() => {
    categoriesAPI.getAll().then(r => {
      const cats = r.data || [];
      cats.forEach(c => {
        if (c.subcategories) {
          c.subcategories.sort((a, b) => a.name.localeCompare(b.name));
        }
      });
      setCategories(cats);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    productsAPI.getAll({ category, subcategory, search, limit: 50 })
      .then(r => setProducts(r.data.products))
      .finally(() => setLoading(false));
  }, [category, subcategory, search]);

  const handleAddToCart = (product) => {
    setToast(`${product.name} added to cart!`);
    setTimeout(() => setToast(''), 2000);
  };

  const setFilters = (updates) => {
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value) params.set(key, value);
      else params.delete(key);
    }
    setSearchParams(params);
  };

  return (
    <div className="container" style={{ padding: '1.5rem 1rem' }}>
      {toast && (
        <div className="alert alert-success" style={{ position: 'fixed', top: 70, right: 20, zIndex: 200, width: 'auto', minWidth: 200 }}>
          ✓ {toast}
        </div>
      )}

      <div className="page-header">
        <h1>Supplies</h1>
        <p>Browse {products.length} products</p>
      </div>

      <div className="products-layout">
        {/* Sidebar Filters */}
        <div className="products-sidebar">
          <div className="card">
            <h3 style={{ fontWeight: 700, marginBottom: '0.75rem', fontSize: '0.9rem' }}>Search</h3>
            <input
              className="form-input w-full"
              placeholder="Search products..."
              value={search}
              onChange={e => setFilters({ search: e.target.value })}
            />

            {/* Mobile Categories (Chips) */}
            <div className="show-mobile" style={{ flexDirection: 'column', marginTop: '0.75rem', width: '100%' }}>
              <div className="chip-list">
                <button 
                  className={`chip ${!category ? 'chip-active' : ''}`}
                  onClick={() => setFilters({ category: '', subcategory: '' })}
                >All Products</button>
                {categories.map(c => (
                  <button 
                    key={c.id}
                    className={`chip ${category === c.slug ? 'chip-active' : ''}`}
                    onClick={() => setFilters({ category: c.slug, subcategory: '' })}
                  >{c.icon} {c.name}</button>
                ))}
              </div>
              {category && categories.find(c => c.slug === category)?.subcategories?.length > 0 && (
                <div className="chip-list" style={{ paddingTop: '0.2rem', marginTop: '-0.2rem' }}>
                  <button
                    className={`chip chip-sub ${!subcategory ? 'chip-sub-active' : ''}`}
                    onClick={() => setFilters({ subcategory: '' })}
                  >All {categories.find(c => c.slug === category)?.name}</button>
                  {categories.find(c => c.slug === category).subcategories.map(sub => (
                    <button
                      key={sub.id}
                      className={`chip chip-sub ${subcategory === sub.slug ? 'chip-sub-active' : ''}`}
                      onClick={() => setFilters({ subcategory: sub.slug })}
                    >{sub.name}</button>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop Categories */}
            <div className="hide-mobile">
              <h3 style={{ fontWeight: 700, margin: '1rem 0 0.5rem', fontSize: '0.9rem' }}>Categories</h3>
              <div className="categories-list">
                <button
                  onClick={() => setFilters({ category: '', subcategory: '' })}
                  style={filterBtnStyle(!category)}
                >All Products</button>
                {categories.map(c => {
                  const isExpanded = expandedCats[c.slug] !== undefined ? expandedCats[c.slug] : category === c.slug;
                  return (
                  <div key={c.id}>
                    <button
                      onClick={() => setFilters({ category: c.slug, subcategory: '' })}
                      style={{
                        ...filterBtnStyle(category === c.slug && !subcategory),
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}
                    >
                      <span>{c.icon} {c.name}</span>
                      {c.subcategories && c.subcategories.length > 0 && (
                        <span 
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedCats(prev => ({...prev, [c.slug]: !isExpanded}));
                          }}
                          style={{ fontSize: '1.2rem', fontWeight: 400, lineHeight: 1, padding: '0 0.4rem', cursor: 'pointer' }}
                        >
                          {isExpanded ? '−' : '+'}
                        </span>
                      )}
                    </button>
                    {isExpanded && c.subcategories && c.subcategories.length > 0 && (
                      <div style={{ paddingLeft: '1.25rem', marginTop: '0.2rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                        {c.subcategories.map(sub => (
                          <button
                            key={sub.id}
                            onClick={() => setFilters({ category: c.slug, subcategory: sub.slug })}
                            style={{
                              ...filterBtnStyle(category === c.slug && subcategory === sub.slug),
                              fontSize: '0.78rem', padding: '0.25rem 0.5rem', 
                              color: category === c.slug && subcategory === sub.slug ? '#1d4ed8' : '#6b7280'
                            }}
                          >
                            ↳ {sub.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )})}
              </div>
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="products-main">
          {loading ? (
            <LoadingSpinner />
          ) : products.length === 0 ? (
            <EmptyState 
              icon={category && !search ? "⏳" : "🔍"} 
              title={category && !search ? "Coming soon" : "No products found"} 
              message={category && !search ? "" : "Try a different search or category"} 
            />
          ) : (
            <div className="grid-4">
              {products.map(p => (
                <ProductCard key={p.id} product={p} onAddToCart={handleAddToCart} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const filterBtnStyle = (active) => ({
  display: 'block', width: '100%',
  textAlign: 'left', padding: '0.4rem 0.65rem', borderRadius: 6, fontSize: '0.83rem',
  background: active ? '#dbeafe' : 'transparent', color: active ? '#1d4ed8' : '#374151',
  fontWeight: active ? 700 : 400, border: 'none', cursor: 'pointer', transition: 'background 0.15s',
});

export default ProductsPage;
